/**
 * EntitySettings — per-entity display-styling preferences (singleton).
 *
 * Mounted at: OrganisationView -> Masters tab -> OrgMasters -> Organisation
 *   group -> "Settings" leaf. ONE record per entity, edit-in-place (GET current,
 *   PUT to update) — mirrors the entity Profile pattern, not a list CRUD.
 * API:  GET/PUT  /api/v1/org/settings  (gated on ORG_ENTITY_PROFILE).
 * Scope: collected & stored only — NOT yet consumed for rendering anywhere.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import Toast, { ToastMessage } from '../common/Toast';
import { usePermission } from '../../contexts/PermissionContext';
import {
  EntitySettings as EntitySettingsType,
  getEntitySettings,
  updateEntitySettings,
} from '../../services/orgService';
import { setEntityDateFormat } from '../../utils/dateSettings';
import CodePrefixSettings from './CodePrefixSettings';
import AiSettings from './AiSettings';

// Settings are another facet of the entity's own profile — they share the
// ORG_ENTITY_PROFILE activity that already gates the profile/GST/TDS endpoints.
const RBAC_ACTIVITY = 'ORG_ENTITY_PROFILE';

// Common currencies — selecting one auto-fills the symbol (still editable).
const CURRENCIES: { code: string; symbol: string; label: string }[] = [
  { code: 'INR', symbol: '₹',  label: 'Indian Rupee (INR)' },
  { code: 'USD', symbol: '$',  label: 'US Dollar (USD)' },
  { code: 'EUR', symbol: '€',  label: 'Euro (EUR)' },
  { code: 'GBP', symbol: '£',  label: 'British Pound (GBP)' },
  { code: 'JPY', symbol: '¥',  label: 'Japanese Yen (JPY)' },
  { code: 'AED', symbol: 'د.إ', label: 'UAE Dirham (AED)' },
  { code: 'SGD', symbol: 'S$', label: 'Singapore Dollar (SGD)' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar (AUD)' },
];

const LANGUAGES: { value: string; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
];

type FormState = Pick<
  EntitySettingsType,
  'currency_code' | 'currency_symbol' | 'number_format' | 'decimal_places' | 'date_format' | 'default_language'
>;

const EntitySettings: React.FC = () => {
  const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
  const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
  const canView = viewPerm.allowed;
  const canModify = modifyPerm.allowed;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [form, setForm] = useState<FormState | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const s = await getEntitySettings();
      setForm({
        currency_code: s.currency_code,
        currency_symbol: s.currency_symbol,
        number_format: s.number_format,
        decimal_places: s.decimal_places,
        date_format: s.date_format,
        default_language: s.default_language,
      });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message ?? 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => (prev ? { ...prev, [key]: value } : prev));

  const onCurrencyChange = (code: string) => {
    const preset = CURRENCIES.find(c => c.code === code);
    setForm(prev => prev ? { ...prev, currency_code: code, currency_symbol: preset?.symbol ?? prev.currency_symbol } : prev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !canModify) return;
    setSaving(true);
    try {
      await updateEntitySettings(form);
      setEntityDateFormat(form.date_format);
      setToast({ type: 'success', message: 'Settings updated.' });
      refresh();
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message ?? 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (!viewPerm.loading && !canView) {
    // The AI Assistant block has its own, hard-coded entitlement (Entity Administrator / Super Admin), so an
    // Entity Administrator without the display-settings grant still reaches it here.
    return (
      <div className="netbody-card flex flex-col h-full">
        <div className="netbody-content"><div className="scroll-container flex-1 overflow-auto">
          <div className="flex items-center justify-center min-h-[200px]">
            <NoDataFound title="Not authorised" message="You do not have permission to view entity settings." />
          </div>
          <AiSettings />
        </div></div>
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-auto">
          {loading || !form ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-1" data-testid="SKEL_org_settings">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-2">
                  <Skeleton.Box width="40%" height={12} />
                  <Skeleton.Box width="100%" height={36} radius={8} />
                </div>
              ))}
            </div>
          ) : (
            <form id="entity-settings-form" onSubmit={handleSubmit} className="p-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                <FormControl.SearchSelect
                  label="Currency"
                  value={form.currency_code}
                  onChange={v => onCurrencyChange(String(v))}
                  options={CURRENCIES.map(c => ({ value: c.code, label: `${c.label} — ${c.symbol}` }))}
                  disabled={!canModify}
                  data-testid="SEL_org_settings_currency"
                />

                <FormControl.Input
                  label="Currency Symbol"
                  type="text"
                  maxLength={8}
                  value={form.currency_symbol}
                  onChange={e => set('currency_symbol', e.target.value)}
                  disabled={!canModify}
                  helperText="Auto-filled from currency; override if needed."
                  data-testid="INP_org_settings_currency_symbol"
                />

                <FormControl.SearchSelect
                  label="Number / Comma Style"
                  value={form.number_format}
                  onChange={v => set('number_format', v as FormState['number_format'])}
                  options={[
                    { value: 'INDIAN', label: 'Indian (12,34,567)' },
                    { value: 'INTERNATIONAL', label: 'International (1,234,567)' },
                  ]}
                  disabled={!canModify}
                  helperText="Indian: 12,34,567 · International: 1,234,567"
                  data-testid="SEL_org_settings_number_format"
                />

                <FormControl.SearchSelect
                  label="Decimal Places"
                  value={String(form.decimal_places)}
                  onChange={v => set('decimal_places', Number(v))}
                  options={[0, 1, 2, 3, 4].map(n => ({ value: String(n), label: String(n) }))}
                  disabled={!canModify}
                  data-testid="SEL_org_settings_decimals"
                />

                <FormControl.SearchSelect
                  label="Date Format"
                  value={form.date_format}
                  onChange={v => set('date_format', v as FormState['date_format'])}
                  options={[
                    { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
                    { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY' },
                    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
                  ]}
                  disabled={!canModify}
                  data-testid="SEL_org_settings_date_format"
                />

                <FormControl.SearchSelect
                  label="Default Language"
                  value={form.default_language}
                  onChange={v => set('default_language', String(v))}
                  options={LANGUAGES.map(l => ({ value: l.value, label: l.label }))}
                  disabled={!canModify}
                  data-testid="SEL_org_settings_language"
                />
              </div>

              {canModify && (
                <div className="flex justify-end mt-6">
                  <Button
                    type="submit"
                    form="entity-settings-form"
                    variant="update"
                    icon={Check}
                    disabled={saving}
                    data-testid="BTN_org_settings_submit"
                  >
                    {saving ? 'Updating…' : 'Update'}
                  </Button>
                </div>
              )}
            </form>
          )}
          <CodePrefixSettings />
          <AiSettings />
        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default EntitySettings;
